import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip
} from '@mui/material';
import { AddRounded, CollectionsRounded, DeleteOutlineRounded, EditRounded } from '@mui/icons-material';
import { FlowBreadcrumbs, FlowPageHeader, FlowStatGrid, SoftPanel, mediaBaseUrl } from './FlowLayout';
import { fetchDestinationHierarchy } from './flowHierarchy';

const ManageGallery = () => {
  const { destinations_id } = useParams();
  const [galleryItems, setGalleryItems] = useState([]);
  const [hierarchy, setHierarchy] = useState({
    destinationName: '',
    tourTypeId: '',
    tourTypeName: '',
    continentId: '',
    continentName: ''
  });
  const [selectedGalleryItemId, setSelectedGalleryItemId] = useState(null);
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [search, setSearch] = useState('');

  useEffect(() => {
    axios.get(`https://aaryanholidays.in/backend/gallery/${destinations_id}`)
      .then((response) => setGalleryItems(response.data))
      .catch((error) => console.error('There was an error fetching the gallery items!', error));
    fetchDestinationHierarchy(destinations_id)
      .then((response) => setHierarchy(response))
      .catch((error) => console.error('There was an error fetching the destination context!', error));
  }, [destinations_id]);

  const filteredGallery = useMemo(() => {
    return galleryItems.filter((item) =>
      String(item.alt || '').toLowerCase().includes(search.toLowerCase())
    );
  }, [galleryItems, search]);

  const handleDelete = (id) => {
    axios.delete(`https://aaryanholidays.in/backend/gallery/${id}`)
      .then(() => setGalleryItems((current) => current.filter((item) => item.id !== id)))
      .catch((error) => console.error('There was an error deleting the gallery item!', error));
    setOpenConfirmDialog(false);
    setSelectedGalleryItemId(null);
  };

  return (
    <Box>
      <FlowBreadcrumbs
        items={[
          { label: 'Tour Management', to: '/manage-continents' },
          { label: hierarchy.continentName || 'Continent', to: hierarchy.continentId ? `/manage-tour-type/${hierarchy.continentId}` : undefined },
          { label: hierarchy.tourTypeName || 'Country', to: hierarchy.tourTypeId ? `/manage-destination/${hierarchy.tourTypeId}` : undefined },
          { label: hierarchy.destinationName || 'Package', to: hierarchy.tourTypeId ? `/manage-destination/${hierarchy.tourTypeId}` : undefined },
          { label: 'Gallery' }
        ]}
      />

      <FlowPageHeader
        title="Manage Gallery"
        subtitle="Keep package media organized with faster access to image ordering and replacements."
        search={search}
        searchPlaceholder="Search alt text or media order"
        onSearchChange={setSearch}
        action={(
          <Button
            component={Link}
            to={`/manage-gallery/add/${destinations_id}`}
            variant="contained"
            startIcon={<AddRounded />}
            sx={{ borderRadius: 999, px: 2.5, py: 1.2, textTransform: 'none', fontWeight: 700 }}
          >
            Add Media
          </Button>
        )}
      />

      <FlowStatGrid items={[{ label: 'Gallery Items', value: galleryItems.length, icon: CollectionsRounded, tone: 'linear-gradient(135deg, #7c3aed 0%, #c084fc 100%)' }]} />

      <SoftPanel>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Preview</TableCell>
                <TableCell>Alt</TableCell>
                <TableCell>Order</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredGallery.map((item) => (
                <TableRow key={item.id} hover>
                  <TableCell>{item.id}</TableCell>
                  <TableCell>
                    {item.type === 'img' ? (
                      <img src={`${mediaBaseUrl}${item.src}`} alt={item.alt} style={{ width: 72, height: 72, objectFit: 'cover', borderRadius: 16 }} />
                    ) : (
                      <video src={`${mediaBaseUrl}${item.src}`} style={{ width: 72, height: 72, objectFit: 'cover', borderRadius: 16 }} controls />
                    )}
                  </TableCell>
                  <TableCell>{item.alt}</TableCell>
                  <TableCell>{item.image_order}</TableCell>
                  <TableCell align="right">
                    <Tooltip title="Edit media">
                      <IconButton component={Link} to={`/manage-gallery/edit/${item.id}`}>
                        <EditRounded />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete media">
                      <IconButton onClick={() => { setSelectedGalleryItemId(item.id); setOpenConfirmDialog(true); }}>
                        <DeleteOutlineRounded />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </SoftPanel>

      <Dialog open={openConfirmDialog} onClose={() => setOpenConfirmDialog(false)}>
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <DialogContentText>Are you sure you want to delete this gallery item? This action cannot be undone.</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenConfirmDialog(false)}>Cancel</Button>
          <Button onClick={() => handleDelete(selectedGalleryItemId)} autoFocus>Confirm</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ManageGallery;
